import { PageController } from '@client/components/_core/layout/PageController';
import type { ComposeState } from '@client/components/_shared/comms/prefill';
import { EmailThreadViewer, type OpenThreadDraft } from '@client/components/_shared/email/Emails/EmailThreadViewer';
import { WmDraftComposer } from '@client/components/_shared/email/Emails/WmDraftComposer';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { trpc } from '@client/trpc/client';
import { skipToken } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Modal } from 'antd';
import { useEffect, useState } from 'react';

export const Route = createFileRoute('/console/$orgId/claims/details/$id/comms/message/$messageId')({
    component: MessageView,
    params: {
        parse({ messageId }) {
            return {
                messageId: BigInt(messageId),
            };
        },
    },
});

function MessageView() {
    const orgId = useOrgId();
    const { messageId, id: claimId } = Route.useParams();
    const navigate = useNavigate();
    const { trpcUtils } = useTrpc();

    const [open, setOpen] = useState(true);
    const [draftCompose, setDraftCompose] = useState<{
        threadId: bigint;
        entityId: bigint;
        draftId: bigint;
        fallbackCompose: ComposeState;
    } | null>(null);
    const draftThreadQuery = trpc.email.getEmailThread.useQuery(
        draftCompose ? { id: draftCompose.threadId } : skipToken,
        { refetchInterval: 1000 * 15 },
    );
    const draftClaimIsClosed =
        draftCompose !== null &&
        draftThreadQuery.data?.relatedEntity?.type === 'claim' &&
        draftThreadQuery.data.relatedEntity.id === draftCompose.entityId &&
        draftThreadQuery.data.relatedEntity.isOpen === false;

    useEffect(() => {
        if (draftClaimIsClosed) setDraftCompose(null);
    }, [draftClaimIsClosed]);

    const handleCloseModal = () => {
        setOpen(false);
    };

    const handleAfterClose = () => {
        void trpcUtils.communication.listCommLogAndNotes.invalidate({ claimId: BigInt(claimId) });
        navigate({
            to: '/console/$orgId/claims/details/$id/comms',
            params: {
                orgId,
                id: claimId,
            },
            fromComponent: 'drawer',
        });
    };

    return (
        <PageController route={Route}>
            <Modal
                open={open}
                width={1200}
                footer={false}
                centered
                styles={{
                    body: {
                        height: 'calc(100vh - 200px)',
                        overflow: 'hidden',
                        padding: '0 0 8px',
                    },
                    container: {
                        padding: 0,
                        overflow: 'hidden',
                        background: 'var(--color-muted)',
                    },
                    footer: { margin: 0 },
                }}
                onCancel={handleCloseModal}
                afterClose={handleAfterClose}
                destroyOnHidden
            >
                <EmailThreadViewer
                    selectedId={messageId}
                    filters={{}}
                    onOpenDraft={({ threadId, entityId, draftId, fallbackCompose }: OpenThreadDraft) =>
                        setDraftCompose({ threadId, entityId, draftId, fallbackCompose })
                    }
                    onClose={handleCloseModal}
                    hideHeaderClose
                    allowForward={false}
                />
            </Modal>
            {draftCompose && !draftClaimIsClosed && (
                <WmDraftComposer
                    entity="claim"
                    entityId={draftCompose.entityId}
                    draftId={draftCompose.draftId}
                    titleOverride="Reply"
                    fallbackCompose={draftCompose.fallbackCompose}
                    onClose={() => {
                        setDraftCompose(null);
                        void trpcUtils.email.getEmailThread.invalidate({ id: messageId });
                    }}
                    onSent={() => {
                        setDraftCompose(null);
                        void trpcUtils.email.getEmailThread.invalidate({ id: messageId });
                        void trpcUtils.communication.listCommLogAndNotes.invalidate({
                            claimId: draftCompose.entityId,
                        });
                    }}
                />
            )}
        </PageController>
    );
}
